import PropTypes from 'prop-types';
import React, { useMemo, useReducer, useState } from 'react';
import { Button, Card, Checkbox, TextInput } from '@opendate-app/setlist';
import AdminPageHead from '../../AdminShared/AdminPageHead';
import client from '../../../shared/api/client';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import { defaultLabels, layoutReducer, toPayload } from '../layoutReducer';
import LayoutEditor from './LayoutEditor';
import NavPreview from './NavPreview';
import '../styles/navigationTypeEditor.css';

const NavigationTypeEditor = ({ navigationType, registry, updateUrl, indexUrl }) => {
  const [state, dispatch] = useReducer(layoutReducer, navigationType);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const labels = useMemo(() => defaultLabels(registry), [registry]);

  const setField = (field) => (value) => dispatch({ type: 'setField', field, value });

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      await client.put(updateUrl, toPayload(state));
      toastSuccess(`Saved ${state.name}`);
    } catch (error) {
      setErrors(error?.response?.data?.errors || ['Could not save. Try again.']);
      toastError('Could not save. See the errors at the top.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ntype-editor">
      <AdminPageHead title={`Edit Navigation Type: ${navigationType.name}`} subtitle={<code>{navigationType.key}</code>}>
        <Button variant="secondary" onClick={() => { window.location.href = indexUrl; }}>All navigation types</Button>
      </AdminPageHead>

      {errors.length > 0 ? (
        <Card className="ntype-errors">
          <ul>{errors.map((message) => <li key={message}>{message}</li>)}</ul>
        </Card>
      ) : null}

      <Card className="ntype-fields">
        <TextInput label="Name" value={state.name} onChange={(event) => setField('name')(event.target.value)} />
        <TextInput label="Fan label" value={state.contactLabel} onChange={(event) => setField('contactLabel')(event.target.value)} />
        <TextInput
          label="AI segment placeholder"
          multiline
          rows={3}
          value={state.segmentPlaceholder}
          onChange={(event) => setField('segmentPlaceholder')(event.target.value)}
        />
        <Checkbox
          label="Show artist-fan features"
          checked={state.artistFanFeatures}
          onChange={(event) => setField('artistFanFeatures')(event.target.checked)}
        />
      </Card>

      <div className="ntype-columns">
        <Card className="ntype-layout-card">
          <h3>Navigation layout</h3>
          <LayoutEditor layout={state.layout} registry={registry} labels={labels} dispatch={dispatch} />
        </Card>
        <NavPreview layout={state.layout} registry={registry} labels={labels} contactLabel={state.contactLabel} />
      </div>

      <div className="ntype-actions">
        <Button variant="primary" onClick={save} disabled={saving}>Update Navigation Type</Button>
      </div>
    </div>
  );
};

NavigationTypeEditor.propTypes = {
  navigationType: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    contactLabel: PropTypes.string.isRequired,
    segmentPlaceholder: PropTypes.string.isRequired,
    artistFanFeatures: PropTypes.bool.isRequired,
    layout: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  registry: PropTypes.object.isRequired,
  updateUrl: PropTypes.string.isRequired,
  indexUrl: PropTypes.string.isRequired,
};

export default NavigationTypeEditor;
